import PropTypes from 'prop-types';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { Loading } from '@/design-system/atoms/Loading';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import { arrayNotEmpty } from '@/utils/arrayNotEmpty';
import { loadDependency } from '@/utils/scriptLoader';

import styles from './MyChartLogin.module.scss';

/**
 * The MyChartLogin Component is used to display a title, MyChart login form,
 * and a link list using Epic's container-based widget SDK.
 */
const MyChartLogin = ({
    title = 'Log In to MyChart',
    widgetUrl,
    apiKey,
    widgetSdkUrl,
    postLoginUrl = 'Home',
    linkList = [],
    theme = Theme.NAME.SPECIALTIES,
    ...props
}) => {
    const mychartRef = useRef(null);
    const [isStandAlone, setIsStandAlone] = useState(true);
    const [sdkLoaded, setSdkLoaded] = useState(false);
    const [sdkLoading, setSdkLoading] = useState(false);
    const [widgetError, setWidgetError] = useState(null);
    const uniqueId = useId();
    // Sanitize ID to only allow alphanumeric characters
    const containerId = `mychart-widget-${uniqueId.replace(/[^a-zA-Z0-9]/g, '')}`;

    useEffect(() => {
        if (mychartRef.current?.closest('[data-component-name="card-text"]')) {
            setIsStandAlone(false);
        }
    }, []);

    // Load Epic Widget SDK script
    useEffect(() => {
        if (!widgetSdkUrl) return;

        // Check if SDK already loaded
        if (window.mychartWidget) {
            setSdkLoaded(true);
            return;
        }

        setSdkLoading(true);

        loadDependency(widgetSdkUrl, (success) => {
            setSdkLoading(false);
            if (success) {
                setSdkLoaded(true);
            } else {
                setWidgetError('Failed to load MyChart widget SDK');
            }
        });
    }, [widgetSdkUrl]);

    // Initialize Epic widget after SDK loads
    useEffect(() => {
        if (!sdkLoaded || !window.mychartWidget) return;

        try {
            window.mychartWidget.renderWidget({
                url: widgetUrl,
                apiKey: apiKey,
                widgetType: 'login',
                containerId: containerId,
                additionalParams: {
                    postLoginUrl: postLoginUrl,
                },
            });
        } catch (err) {
            setWidgetError(`Widget initialization failed: ${err.message}`);
        }
    }, [sdkLoaded, widgetUrl, apiKey, postLoginUrl, containerId]);

    /**
     * Renders loading indicator while SDK is being fetched.
     * @returns {React.ReactNode} Loading component
     */
    const renderLoading = useCallback(
        () => (
            <div className={styles['mychart-login__loading']}>
                <Loading description="Loading MyChart login..." />
            </div>
        ),
        [],
    );

    /**
     * Renders error UI when widget SDK fails to load.
     * Provides fallback link to direct MyChart login.
     * @returns {React.ReactNode} Error fallback component
     */
    const renderErrorFallback = useCallback(
        () => (
            <div className={styles['mychart-login__error']}>
                <p>Unable to load MyChart login. Please try again later.</p>
                <Button
                    label="Sign in to MyChart"
                    href={
                        widgetUrl
                            ? `${widgetUrl}/Authentication/Login`
                            : 'https://mychart.atlantichealth.org/MyChart/Authentication/Login'
                    }
                    buttonStyle={Button.STYLE.PRIMARY}
                    target="_blank"
                />
            </div>
        ),
        [widgetUrl],
    );

    /**
     * Renders Epic container-based widget.
     * Shows loading state while SDK fetches, error fallback on failure.
     * @returns {React.ReactNode} Epic widget container or loading/error state
     */
    const renderWidget = useCallback(() => {
        if (widgetError) {
            return renderErrorFallback();
        }

        if (sdkLoading || !sdkLoaded) {
            return renderLoading();
        }

        return (
            <div className={styles['mychart-login__widget']}>
                <div id={containerId} className={styles['mychart-login__epic-container']} />
            </div>
        );
    }, [widgetError, sdkLoading, sdkLoaded, containerId, renderErrorFallback, renderLoading]);

    return (
        <div className={styles['mychart-login']} {...props} ref={mychartRef}>
            <ContentContainer className={styles['mychart-login__wrapper']}>
                <Grid
                    className={styles['mychart-login__grid']}
                    col={{ sm: 4, md: 6, lg: isStandAlone ? 12 : 6 }}
                >
                    <GridColumn
                        colSpan={{ sm: 4, md: 6, lg: isStandAlone ? 8 : 6 }}
                        colStart={{ sm: 1, md: 1, lg: isStandAlone ? 3 : 1 }}
                        className={styles['mychart-login__grid-column']}
                    >
                        <Theme name={theme} className={styles['mychart-login__theme']}>
                            <div className={styles['mychart-login__main']}>
                                {title && (
                                    <Title
                                        className={styles['mychart-login__title']}
                                        content={title}
                                        variant={Title.VARIANT.H5}
                                        tag={Title.TAG.H2}
                                    />
                                )}
                                {renderWidget()}
                                {arrayNotEmpty(linkList) && (
                                    <div className={styles['mychart-login__linklist']}>
                                        {linkList?.map((link, index) => (
                                            <Button
                                                key={index}
                                                {...link}
                                                buttonStyle={Button.STYLE.TEXT}
                                                className={styles['mychart-login__link']}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Theme>
                    </GridColumn>
                </Grid>
            </ContentContainer>
        </div>
    );
};

MyChartLogin.propTypes = {
    /**
     * Title Text
     */
    title: PropTypes.string,
    /**
     * MyChart base URL for Epic widget (e.g., https://mychart.atlantichealth.org/MyChart)
     */
    widgetUrl: PropTypes.string,
    /**
     * Epic Widget API key (environment-specific)
     */
    apiKey: PropTypes.string,
    /**
     * Full URL to Epic widget SDK script
     */
    widgetSdkUrl: PropTypes.string,
    /**
     * Post-login redirect URL/path (default: 'Home')
     */
    postLoginUrl: PropTypes.string,
    /**
     * Array of links displayed below the widget
     */
    linkList: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            href: PropTypes.string,
            ariaLabel: PropTypes.string,
            target: PropTypes.string,
        }),
    ),
    /**
     * Theme for the component
     */
    theme: PropTypes.oneOf(Object.values(Theme.NAME)),
};

export default MyChartLogin;
